"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity, readTemperature } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "refrigerator-door-energy-loss-calculator",
  "energy-cost-calculator",
  "refrigerator-size-calculator",
  "refrigerator-coil-cleaning-savings-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Refrigerator Temperature Recovery Calculator",
  category: "Food Safety" as const,
  mediumDescription:
    "Estimate how long it takes a fridge or freezer to return to safe temperature after a door opening or power blip — and whether food in the cabinet is still safe. Based on USDA food-safety guidance and ASHRAE thermal-recovery measurements.",
  faqs: [
    { question: "What temperature should my fridge be?", answer: "USDA recommends fresh food at 40°F or below and freezer at 0°F or below. The FDA Food Code uses the same 40°F threshold to define the food-temperature 'danger zone' upper limit." },
    { question: "How long is food safe in the danger zone?", answer: "USDA's 2-hour rule: perishable food held above 40°F for more than 2 hours (or above 90°F for more than 1 hour) should be discarded. The clock starts when temperature crosses 40°F, not when the door opens." },
    { question: "How long does a fridge stay cold during a power outage?", answer: "USDA: a closed fridge keeps food safe for ~4 hours. A full freezer holds for 48 hours; a half-full freezer for 24 hours. Recovery times here apply to brief openings, not long outages." },
    { question: "What recovery time is normal?", answer: "ASHRAE thermal-recovery testing shows healthy fridges recover ~2.5 minutes per °F, freezers ~3.5 minutes per °F. Anything dramatically longer suggests a seal, refrigerant, or compressor issue." },
    { question: "Why does freezer recovery take longer per degree?", answer: "Freezers operate at a larger temperature differential to ambient (50–80°F gradient vs 30–40°F for a fridge), so heat infiltrates faster relative to cooling capacity." },
    { question: "Does a full fridge recover faster?", answer: "Yes. Cold mass (food + drinks) holds temperature when the door opens. ENERGY STAR recommends 70–80% full for both energy efficiency and faster recovery." },
  ],
  relatedTools: ["refrigerator-door-energy-loss-calculator", "refrigerator-coil-cleaning-savings-calculator", "refrigerator-size-calculator"],
}

export function TemperatureRecoveryCalculatorPageClient() {
  const [applianceType, setApplianceType] = useState("refrigerator")
  const [currentTemp, setCurrentTemp] = useState("45")
  const [targetTemp, setTargetTemp] = useState("37")
  const [doorOpenTime, setDoorOpenTime] = useState("30")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    // Both temperatures are read in °F and both are legitimately negative — a
    // freezer target of 0 and a deep-freeze reading below it are real entries.
    // −40 sits under the coldest household setpoint, 110 above a failed cabinet
    // sitting at room temperature. Door time is a duration, so it is positive.
    const current = readTemperature(currentTemp, { min: -40, max: 110 })
    const target = readTemperature(targetTemp, { min: -40, max: 110 })
    const doorTime = readQuantity(doorOpenTime, { min: 1, max: 3600 })
    if (current === null || target === null || doorTime === null) return null

    const tempDifference = Math.abs(current - target)

    let minutesPerDegree = 0
    if (applianceType === "refrigerator") minutesPerDegree = 2.5
    else if (applianceType === "freezer") minutesPerDegree = 3.5
    else if (applianceType === "wine-cooler") minutesPerDegree = 2
    else minutesPerDegree = 3

    let recoveryTime = tempDifference * minutesPerDegree
    if (doorTime > 60) recoveryTime *= 1.5
    else if (doorTime > 30) recoveryTime *= 1.25

    let energyImpact = ""
    if (tempDifference < 5) energyImpact = "Minimal — compressor will run slightly longer."
    else if (tempDifference < 10) energyImpact = "Moderate — compressor will run 15–30 minutes extra."
    else if (tempDifference < 20) energyImpact = "Significant — 30–60 minutes of extra runtime."
    else energyImpact = "High — compressor may run continuously for 1–2 hours."

    let foodSafety = ""
    if (applianceType === "refrigerator") {
      if (current > 40 && doorTime > 120) foodSafety = "Danger zone — food above 40°F for >2 hours per USDA: discard."
      else if (current > 40) foodSafety = "Caution — monitor temperature; USDA 2-hour clock starts at 40°F."
      else foodSafety = "Safe — food temperature within USDA range (≤40°F)."
    } else if (applianceType === "freezer") {
      if (current > 32) foodSafety = "Thawing — check for ice-crystal loss; refreeze only if still ≤40°F."
      else if (current > 10) foodSafety = "Warming — still safely frozen but trending up."
      else foodSafety = "Safe — within USDA freezer range (≤0°F)."
    } else {
      foodSafety = "Monitor temperature — confirm rapid return to setpoint."
    }

    return { recoveryTime: Math.round(recoveryTime), tempDifference, energyImpact, foodSafety }
  }, [applianceType, currentTemp, targetTemp, doorOpenTime])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="How long until your fridge gets"
      heroTitleItalic="back"
      heroTitlePost="to safe temp?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~30 sec", "USDA 2-hour rule + ASHRAE"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "40°F", label: "Fridge max", note: "USDA / FDA" },
            { value: "0°F", label: "Freezer target", note: "USDA" },
            { value: "2 hr", label: "Danger zone", note: "USDA discard rule" },
            { value: "2.5 min", label: "Per °F recovery", note: "ASHRAE typical" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "refrigerator-temperature-recovery-calculator")}
      cta={{
        title: "Slow recovery? Time for a fridge with better insulation.",
        body: "Browse bottom-freezer refrigerators at Fridge.com — modern foam-insulated cabinets recover faster and hold temperature longer.",
        buttonLabel: "Browse bottom-freezer fridges",
        buttonHref: "/collections/bottom-freezer-refrigerators",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Four inputs.
                <br />
                Real recovery time.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Temperature differential × ASHRAE recovery rate × door-open multiplier.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Appliance type
                  </label>
                  <select
                    value={applianceType}
                    onChange={(e) => setApplianceType(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="refrigerator">Refrigerator</option>
                    <option value="freezer">Freezer</option>
                    <option value="wine-cooler">Wine cooler</option>
                    <option value="mini-fridge">Mini fridge</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Current temperature
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={-40}
                      max={110}
                      step="1"
                      value={currentTemp}
                      onChange={(e) => setCurrentTemp(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">°F</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Target temperature
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={-40}
                      max={110}
                      step="1"
                      value={targetTemp}
                      onChange={(e) => setTargetTemp(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">°F</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> USDA recommends 35–38°F fridge, 0°F freezer.
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Door open time
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={3600}
                      step="1"
                      value={doorOpenTime}
                      onChange={(e) => setDoorOpenTime(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">sec</span>
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Estimated recovery time
                  </div>
                  <div className="font-black" style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}>
                    {result?.recoveryTime ?? "—"}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">min</span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result?.foodSafety ?? "Enter current and target temps."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Δ Temperature</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.tempDifference ?? "—"}°F</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Energy impact</div>
                      <div className="text-[12px] sm:text-[14px] font-bold leading-[1.35]">{result?.energyImpact?.split(" — ")[0] ?? "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Faster recovery"
          title="Bottom-freezer fridges"
          description="Modern foam-insulated cabinets recover temperature faster and hold longer in outages."
          collectionType="bottom-freezer"
          fallbackCollectionType="french-door"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                <em className="italic text-[#ff9951]">USDA</em> safety. ASHRAE rates.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Recovery rates from ASHRAE Handbook—Refrigeration thermal-recovery measurements. Food-safety thresholds (40°F fridge, 0°F freezer, 2-hour danger-zone rule) from USDA Food Safety and Inspection Service.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Temperature differential", d: "Δ°F = current − target. Drives total cooling work the compressor must do." },
                { n: "02", t: "Recovery rate by appliance", d: "Refrigerator: ~2.5 min/°F. Freezer: ~3.5 min/°F (larger ambient gradient). Wine cooler: ~2 min/°F. Mini fridge: ~3 min/°F." },
                { n: "03", t: "Door-open multiplier", d: "Door open >30 sec: ×1.25. Door open >60 sec: ×1.5. Reflects added infiltration heat the compressor must remove." },
                { n: "04", t: "USDA safety check", d: "Fridge: alert at 40°F (2-hour rule). Freezer: alert at >32°F (thawing). Mini-fridge / wine cooler: monitor return to setpoint." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Top-freezer fridges"
          description="Smaller cabinets, simpler airflow, and quick temperature recovery — best for households that open the door often."
          collectionType="top-freezer"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
